import { ModelMetadata, ModelConfig } from "api/models";
import {
  LLM_MODEL_TYPE_KEYS,
  EMBEDDING_MODEL_TYPE_KEYS,
  LLM_MODEL_TYPE_CONFIGS,
  EMBEDDING_MODEL_TYPE_CONFIGS,
  LLM_ENUM_TO_DISPLAY,
  EMBEDDING_ENUM_TO_DISPLAY,
  LLM_MODEL_NAME_TO_DISPLAY,
  EMBEDDING_MODEL_NAME_TO_DISPLAY,
} from "constants/configuration-models";

// ============================================================================
// Model Type Guards
// ============================================================================

/**
 * Type guard to check if a string is a valid LLM model type
 * @param value - The string value to check
 * @returns True if the value is a valid LLM model type enum value
 */
export function isLLMModelType(value: string): value is LLM_MODEL_TYPE_KEYS {
  return Object.values(LLM_MODEL_TYPE_KEYS).includes(value as LLM_MODEL_TYPE_KEYS);
}

/**
 * Type guard to check if a string is a valid embedding model type
 * @param value - The string value to check
 * @returns True if the value is a valid embedding model type enum value
 */
export function isEmbeddingModelType(value: string): value is EMBEDDING_MODEL_TYPE_KEYS {
  return Object.values(EMBEDDING_MODEL_TYPE_KEYS).includes(value as EMBEDDING_MODEL_TYPE_KEYS);
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Gets all available LLM model display names (enum values)
 */
export function getLLMModelTypes(): readonly LLM_MODEL_TYPE_KEYS[] {
  return Object.values(LLM_MODEL_TYPE_KEYS);
}

/**
 * Gets all available embedding model display names (enum values)
 */
export function getEmbeddingModelTypes(): readonly EMBEDDING_MODEL_TYPE_KEYS[] {
  return Object.values(EMBEDDING_MODEL_TYPE_KEYS);
}

/**
 * Gets the configuration for a specific LLM model type
 * @param displayName - The display name (enum value) of the model
 * @returns The model configuration or undefined if not found
 */
export function getLLMModelConfig(displayName: LLM_MODEL_TYPE_KEYS | string): ModelConfig | undefined {
  return LLM_MODEL_TYPE_CONFIGS[displayName];
}

/**
 * Gets the configuration for a specific embedding model type
 * @param displayName - The display name (enum value) of the model
 * @returns The model configuration or undefined if not found
 */
export function getEmbeddingModelConfig(displayName: EMBEDDING_MODEL_TYPE_KEYS | string): ModelConfig | undefined {
  return EMBEDDING_MODEL_TYPE_CONFIGS[displayName];
}

/**
 * Gets the default LLM model type (first in the enum)
 */
export function getDefaultLLMModelType(): LLM_MODEL_TYPE_KEYS {
  return getLLMModelTypes()[0] || LLM_MODEL_TYPE_KEYS.AZURE_OPENAI_GPT_4_1;
}

/**
 * Gets the default embedding model type (first in the enum)
 */
export function getDefaultEmbeddingModelType(): EMBEDDING_MODEL_TYPE_KEYS {
  return getEmbeddingModelTypes()[0] || EMBEDDING_MODEL_TYPE_KEYS.AZURE_OPENAI_TEXT_EMBEDDING_3_SMALL;
}

// ============================================================================
// Display Name Conversion Functions
// ============================================================================

/**
 * Converts an LLM model enum value or model name to its display name (enum value)
 * 
 * @param enumValue - The enum value or identifier from the API
 * @param modelMetadata - Optional model metadata containing model_name
 * @returns The display name (enum value) for the model, or the original value if no mapping found
 * 
 * @example
 * ```ts
 * getLLMDisplayName("AGENTIQ_AI_1") // Returns LLM_MODEL_TYPE_KEYS.AGENTIQ_AI_1
 * getLLMDisplayName("unknown", { model_name: "agentiqai-1" }) // Returns LLM_MODEL_TYPE_KEYS.AGENTIQ_AI_1
 * ```
 */
export function getLLMDisplayName(enumValue: string, modelMetadata?: ModelMetadata): string {
  // First, try to map enum values
  if (LLM_ENUM_TO_DISPLAY[enumValue]) {
    return LLM_ENUM_TO_DISPLAY[enumValue];
  }
  
  // Try to map by model_name from metadata
  if (modelMetadata?.model_name) {
    const displayName = LLM_MODEL_NAME_TO_DISPLAY[modelMetadata.model_name];
    if (displayName) {
      return displayName;
    }
  }
  
  // Fallback: check if the value itself is already a display name (enum value)
  if (LLM_MODEL_TYPE_CONFIGS[enumValue]) {
    return enumValue;
  }
  
  // Last resort: return the original value
  return enumValue;
}

/**
 * Converts an embedding model enum value or model name to its display name (enum value)
 * 
 * @param enumValue - The enum value or identifier from the API
 * @param modelMetadata - Optional model metadata containing model_name
 * @returns The display name (enum value) for the model, or the original value if no mapping found
 * 
 * @example
 * ```ts
 * getEmbeddingDisplayName("AGENTIQ_AI_EMBEDDING_1") // Returns EMBEDDING_MODEL_TYPE_KEYS.AGENTIQ_AI_EMBEDDING_1
 * getEmbeddingDisplayName("unknown", { model_name: "agentiqai-embedding-1" }) // Returns EMBEDDING_MODEL_TYPE_KEYS.AGENTIQ_AI_EMBEDDING_1
 * ```
 */
export function getEmbeddingDisplayName(enumValue: string, modelMetadata?: ModelMetadata): string {
  // First, try to map enum values
  if (EMBEDDING_ENUM_TO_DISPLAY[enumValue]) {
    return EMBEDDING_ENUM_TO_DISPLAY[enumValue];
  }
  
  // Try to map by model_name from metadata
  if (modelMetadata?.model_name) {
    const displayName = EMBEDDING_MODEL_NAME_TO_DISPLAY[modelMetadata.model_name];
    if (displayName) {
      return displayName;
    }
  }
  
  // Fallback: check if the value itself is already a display name (enum value)
  if (EMBEDDING_MODEL_TYPE_CONFIGS[enumValue]) {
    return enumValue;
  }
  
  // Last resort: return the original value
  return enumValue;
}

